// src/pages/Billing.tsx
import React from 'react';
import { Card, Button, Alert, Space, Tag, Empty, Row, Col } from 'antd';
import { useGetIdentity } from '@refinedev/core';
import { useStripe } from '../hooks/useStripe';
import {
  CreditCardOutlined,
  CheckOutlined,
  RocketOutlined,
  VideoCameraOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  PLANS,
  PaidPlanKey,
  getPlanDisplayName,
  getReviewLimit,
  getOverageRate,
  getOverageCount,
  hasActivePlan,
} from '../utils/planLimits';

const BROKERAGE_EMAIL = 'info@spectreviews.com';

const planCards: Record<PaidPlanKey, { icon: React.ReactNode; features: string[] }> = {
  standard: {
    icon: <VideoCameraOutlined />,
    features: [
      '3 reports included / month',
      'Then $10 per additional report',
      'Custom branding',
      'Client collaboration',
    ],
  },
  pro: {
    icon: <RocketOutlined />,
    features: [
      '20 reports included / month',
      'Then $6 per additional report',
      'Custom branding',
      'Priority support',
    ],
  },
};

const Billing: React.FC = () => {
  const { data: user } = useGetIdentity<any>();
  const { createCheckoutSession, createCustomerPortalSession, loading } = useStripe();

  const handleSubscribe = (planKey: PaidPlanKey) => {
    createCheckoutSession(planKey);
  };

  const handleManageSubscription = () => {
    createCustomerPortalSession();
  };

  const currentPlan: string = user?.plan || 'free';
  const isActive = hasActivePlan(currentPlan);
  const used = user?.usage?.videosThisMonth || 0;
  const included = getReviewLimit(currentPlan);
  const overageCount = getOverageCount(currentPlan, used);
  const overageRate = getOverageRate(currentPlan);
  const overageCost = overageCount * overageRate;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      {/* Current Subscription */}
      <Card
        title={
          <Space>
            <CreditCardOutlined />
            <span>Current Subscription</span>
          </Space>
        }
        style={{ marginBottom: '24px' }}
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} md={8}>
            <div style={{ marginBottom: '8px', color: '#666' }}>Plan</div>
            <span style={{ fontSize: '24px', fontWeight: 600 }}>
              {getPlanDisplayName(currentPlan)}
            </span>
          </Col>

          <Col xs={24} md={8}>
            <div style={{ marginBottom: '8px', color: '#666' }}>Status</div>
            <Tag color={isActive ? 'success' : 'default'} style={{ fontSize: '14px', padding: '4px 12px' }}>
              {isActive ? 'Active' : 'No active plan'}
            </Tag>
          </Col>

          {isActive && (
            <Col xs={24} md={8}>
              <div style={{ marginBottom: '8px', color: '#666' }}>This Month</div>
              <div style={{ fontSize: '16px' }}>
                <div>{used} report{used === 1 ? '' : 's'} · {included} included</div>
                {overageCount > 0 && (
                  <div style={{ color: '#d46b08' }}>
                    {overageCount} overage × ${overageRate} = ${overageCost}
                  </div>
                )}
              </div>
            </Col>
          )}
        </Row>

        <div style={{ marginTop: '32px' }}>
          {isActive ? (
            <Button
              type="primary"
              onClick={handleManageSubscription}
              loading={loading}
              icon={<CreditCardOutlined />}
              size="large"
            >
              Manage Subscription
            </Button>
          ) : (
            <Alert
              message="No active plan"
              description="Choose a plan below to start creating reviews. Your first month's base is free."
              type="info"
              showIcon
            />
          )}
        </div>
      </Card>

      {/* Plan options (shown when the user has no active paid plan) */}
      {!isActive && (
        <Card title="Choose Your Plan" style={{ marginBottom: '24px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px',
            }}
          >
            {(Object.keys(PLANS) as PaidPlanKey[]).map((key) => {
              const plan = PLANS[key];
              return (
                <Card
                  key={key}
                  hoverable
                  style={{ height: '100%' }}
                  actions={[
                    <Button
                      type="primary"
                      size="large"
                      onClick={() => handleSubscribe(key)}
                      loading={loading}
                      block
                    >
                      Choose {plan.name}
                    </Button>,
                  ]}
                >
                  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <div style={{ fontSize: '32px', color: '#1890ff', marginBottom: '12px' }}>
                      {planCards[key].icon}
                    </div>
                    <h3 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>{plan.name}</h3>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1890ff', marginTop: '8px' }}>
                      {plan.price}
                    </div>
                    <div style={{ color: '#52c41a', marginTop: '4px', fontSize: '13px' }}>
                      First month base free
                    </div>
                  </div>

                  <div>
                    {planCards[key].features.map((feature, index) => (
                      <div
                        key={index}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}
                      >
                        <CheckOutlined style={{ color: '#52c41a' }} />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}

            {/* Brokerage — contact sales */}
            <Card
              hoverable
              style={{ height: '100%' }}
              actions={[
                <Button size="large" block href={`mailto:${BROKERAGE_EMAIL}`}>
                  Contact Us
                </Button>,
              ]}
            >
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ fontSize: '32px', color: '#1890ff', marginBottom: '12px' }}>
                  <TeamOutlined />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>Brokerage</h3>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff', marginTop: '8px' }}>
                  Let's talk
                </div>
                <div style={{ color: '#666', marginTop: '4px', fontSize: '13px' }}>
                  Multi-user accounts
                </div>
              </div>
              <div>
                {['Multiple users, one account', 'Volume pricing', 'Dedicated support'].map((feature, index) => (
                  <div
                    key={index}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}
                  >
                    <CheckOutlined style={{ color: '#52c41a' }} />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </Card>
      )}

      {/* Billing History - only for active subscribers */}
      {isActive && (
        <Card title="Billing History">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size="small">
                <span>Invoices and payment history are managed through Stripe</span>
                <Button type="primary" onClick={handleManageSubscription} icon={<CreditCardOutlined />}>
                  Open Billing Portal
                </Button>
              </Space>
            }
          />
        </Card>
      )}
    </div>
  );
};

export default Billing;
